import { logError, logInfo } from '../../../utils/logger';

// Calculate K-factor (vertical curvature)
export const calculateKFactor = (point1, point2, point3) => {
  const x1 = point1.distance;
  const y1 = point1.elevation;
  const x2 = point2.distance;
  const y2 = point2.elevation;
  const x3 = point3.distance;
  const y3 = point3.elevation;

  // Calculate horizontal distance
  const L = x3 - x1;
  
  // Calculate elevation difference
  const g1 = (y2 - y1) / (x2 - x1) * 100; // Grade in percent
  const g2 = (y3 - y2) / (x3 - x2) * 100;
  
  // Calculate algebraic difference in grades
  const A = Math.abs(g2 - g1);
  
  // K = L/A
  return A === 0 ? Infinity : L / A;
};

// Calculate vertical curve radius
export const calculateRadius = (kFactor, A) => {
  return kFactor * A / 8;
};

// Calculate minimum clearance required
export const calculateMinClearance = (radius, axleSpacing) => {
  return radius - Math.sqrt(radius * radius - (axleSpacing / 2) * (axleSpacing / 2));
};

// Check for clearance issues
export const analyzeClearance = (points, convoySpecs) => {
  const issues = [];
  const { length, clearance, axleSpacing, speed, suspension } = convoySpecs;

  // Get rear height and contact point from convoy specs
  const rearHeight = convoySpecs.rearHeight || clearance;
  const rearContact = convoySpecs.rearContact || 2.5;
  const rearOverhang = length - (axleSpacing + rearContact);
  const rearPoint = {
    distance: rearOverhang,
    height: rearHeight
  };

  // Minimum required K-factors based on convoy specs
  const minCrestK = length * length / (2 * clearance);
  
  // Calculate minimum sag K-factor considering both axle spacing and rear overhang
  // For axle spacing: K = L²/2h where L is axle spacing and h is clearance
  const minSagKAxle = Math.pow(axleSpacing, 2) / (2 * clearance);
  
  // For rear overhang: K = L²/2h where L is overhang and h is rear height
  const minSagKRear = Math.pow(rearOverhang, 2) / (2 * rearHeight);
  
  // Use the more restrictive K-factor
  const minSagK = Math.max(minSagKAxle, minSagKRear);

  // Dynamic factor based on speed and suspension
  const getDynamicFactor = () => {
    const speedFactor = Math.pow(speed / 50, 2); // Normalized to 50 km/h
    const suspensionFactors = {
      air: 1.1,
      leaf: 1.3,
      hydraulic: 1.2
    };
    return speedFactor * (suspensionFactors[suspension] || 1.2);
  };

  const dynamicFactor = getDynamicFactor();

  // Analyze each set of three consecutive points
  for (let i = 0; i < points.length - 2; i++) {
    const kFactor = calculateKFactor(points[i], points[i + 1], points[i + 2]);
    const g1 = (points[i + 1].elevation - points[i].elevation) / (points[i + 1].distance - points[i].distance) * 100;
    const g2 = (points[i + 2].elevation - points[i + 1].elevation) / (points[i + 2].distance - points[i + 1].distance) * 100;
    const A = Math.abs(g2 - g1);
    const radius = calculateRadius(kFactor, A);
    const minRequired = calculateMinClearance(radius, axleSpacing);
    
    // Check for crest curves (convex)
    if (points[i + 1].elevation > points[i].elevation && 
        points[i + 1].elevation > points[i + 2].elevation) {
      if (kFactor < minCrestK * dynamicFactor) {
        issues.push({
          type: 'crest',
          severity: kFactor < minCrestK * 0.8 ? 'critical' : 'warning',
          distance: points[i + 1].distance,
          kFactor,
          radius,
          minRequired,
          A,
          message: 'Convoy may bottom out at crest',
          details: {
            requiredK: minCrestK * dynamicFactor,
            actualK: kFactor,
            requiredR: minCrestK * dynamicFactor * A / 8,
            actualR: radius,
            clearance: clearance,
            minClearance: minRequired
          }
        });
      }
    }
    
    // Check for sag curves (concave)
    if (points[i + 1].elevation < points[i].elevation && 
        points[i + 1].elevation < points[i + 2].elevation) {
      
      // Calculate clearance at both axle and rear point
      const axleClearance = calculateMinClearance(radius, axleSpacing);
      const rearClearance = calculateMinClearance(radius, rearOverhang);
      
      // Use the more critical clearance value
      const criticalClearance = Math.min(
        axleClearance / clearance, // Normalize by design clearance
        rearClearance / rearHeight // Normalize by rear height
      );

      if (kFactor < minSagK * dynamicFactor) {
        issues.push({
          type: 'sag',
          severity: criticalClearance < 0.8 ? 'critical' : 'warning',
          distance: points[i + 1].distance,
          kFactor,
          radius,
          minRequired: Math.max(axleClearance, rearClearance),
          A,
          message: 'Trailer may scrape in sag curve',
          details: {
            requiredK: minSagK * dynamicFactor,
            requiredKAxle: minSagKAxle * dynamicFactor,
            requiredKRear: minSagKRear * dynamicFactor,
            actualK: kFactor,
            requiredR: minSagK * dynamicFactor * A / 8,
            actualR: radius,
            clearance: clearance,
            rearHeight: rearHeight,
            rearOverhang: rearOverhang,
            minClearance: minRequired
          }
        });
      }
    }
  }

  return issues;
};

// Export formulas for UI display
export const CLEARANCE_FORMULAS = {
  kFactor: {
    formula: 'K = L/A',
    variables: {
      K: 'Vertical curve K-factor (m/%)',
      L: 'Curve length (m)',
      A: 'Absolute slope difference (%)'
    }
  },
  radius: {
    formula: 'R = K×A/8',
    variables: {
      R: 'Radius of the curve (m)',
      K: 'K-factor (m/%)',
      A: 'Absolute slope difference (%)'
    }
  },
  minClearance: {
    formula: 'Cmin = R - √(R² - (Lw/2)²)',
    variables: {
      'Cmin': 'Minimum required clearance (m)',
      R: 'Radius of curve (m)',
      Lw: 'Axle spacing (m)'
    }
  }
};